import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Droplets, Footprints, Moon, Brain, Utensils, Book, Dumbbell, Heart, Smile, Coffee } from 'lucide-react';

const PRESET_COLORS = [
  { name: 'Coral', value: '#FF6B6B' },
  { name: 'Ocean', value: '#4ECDC4' },
  { name: 'Lavender', value: '#95A6F8' },
  { name: 'Mint', value: '#7BC9A6' },
  { name: 'Peach', value: '#FFB088' },
  { name: 'Sky', value: '#74C0FC' },
];

const HABIT_ICONS = [
  { name: 'Droplets', icon: Droplets },
  { name: 'Footprints', icon: Footprints },
  { name: 'Moon', icon: Moon },
  { name: 'Brain', icon: Brain },
  { name: 'Utensils', icon: Utensils },
  { name: 'Book', icon: Book },
  { name: 'Dumbbell', icon: Dumbbell },
  { name: 'Heart', icon: Heart },
  { name: 'Smile', icon: Smile },
  { name: 'Coffee', icon: Coffee },
];

const UNITS = ['ml', 'L', 'passos', 'min', 'horas', 'km', 'páginas', 'vezes', 'porções'];

const HabitForm = ({ onSubmit, initialData, onCancel }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [category, setCategory] = useState(initialData?.category || '');
  const [color, setColor] = useState(initialData?.color || PRESET_COLORS[0].value);
  const [icon, setIcon] = useState(initialData?.icon || 'Droplets');
  const [target, setTarget] = useState(initialData?.target || '');
  const [unit, setUnit] = useState(initialData?.unit || 'vezes');
  const [frequencyType, setFrequencyType] = useState(initialData?.type || initialData?.frequency?.type || 'daily');
  const [weekDays, setWeekDays] = useState(initialData?.weekDays || initialData?.frequency?.weekDays || []);
  const [monthDay, setMonthDay] = useState(initialData?.monthDay || initialData?.frequency?.monthDay || 1);
  const [reminderTime, setReminderTime] = useState(initialData?.reminderTime || initialData?.frequency?.reminderTime || '');
  const [reminderFrequency, setReminderFrequency] = useState(initialData?.reminderFrequency || 'once');
  const [reminderInterval, setReminderInterval] = useState(initialData?.reminderInterval || '2');
  const [description, setDescription] = useState(initialData?.description || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    const habitData = {
      name: name.trim(),
      category: category.trim(),
      color,
      icon,
      target: target ? parseInt(target) : null,
      unit: target ? unit : null,
      reminderTime: reminderTime || null,
      reminderFrequency: reminderTime ? reminderFrequency : null,
      reminderInterval: reminderTime && reminderFrequency === 'interval' ? parseInt(reminderInterval) : null,
      reminderInterval: reminderTime && reminderFrequency === 'interval' ? parseInt(reminderInterval) : null,
      frequencyType,
      weekDays: frequencyType === 'weekly' ? weekDays : null,
      monthDay: frequencyType === 'monthly' ? parseInt(monthDay) : null,
      description: description.trim(),
    };

    if (initialData?.id) {
      habitData.id = initialData.id;
      habitData.createdAt = initialData.createdAt;
    }

    onSubmit(habitData);
    setName('');
    setDescription('');
    setCategory('');
    setColor(PRESET_COLORS[0].value);
    setIcon('Droplets');
    setTarget('');
    setUnit('vezes');
    setUnit('vezes');
    setFrequencyType('daily');
    setWeekDays([]);
    setMonthDay(1);
  };

  const toggleWeekDay = (dayIndex) => {
    setWeekDays(prev =>
      prev.includes(dayIndex)
        ? prev.filter(d => d !== dayIndex)
        : [...prev, dayIndex]
    );
  };

  const WEEK_DAYS = [
    { label: 'D', value: 0 },
    { label: 'S', value: 1 },
    { label: 'T', value: 2 },
    { label: 'Q', value: 3 },
    { label: 'Q', value: 4 },
    { label: 'S', value: 5 },
    { label: 'S', value: 6 },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="name">Nome do Hábito</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ex: Beber água"
          required
          autoFocus
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Categoria</Label>
        <Input
          id="category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="ex: Saúde, Produtividade"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Anotações (Opcional)</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Adicione detalhes, motivações ou instruções..."
          className="resize-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="target">Meta (opcional)</Label>
          <Input
            id="target"
            type="number"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="ex: 2000"
            min="0"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="unit">Unidade</Label>
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {UNITS.map((u) => (
                <SelectItem key={u} value={u}>
                  {u}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="frequencyType">Frequência</Label>
        <Select value={frequencyType} onValueChange={setFrequencyType}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Diário</SelectItem>
            <SelectItem value="weekly">Semanal</SelectItem>
            <SelectItem value="monthly">Mensal</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {frequencyType === 'weekly' && (
        <div className="space-y-2">
          <Label>Dias da Semana</Label>
          <div className="flex justify-between gap-1">
            {WEEK_DAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                onClick={() => toggleWeekDay(day.value)}
                className={`w-9 h-9 rounded-full text-sm font-medium transition-colors border ${weekDays.includes(day.value)
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-input hover:bg-accent'
                  }`}
              >
                {day.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Selecione os dias para realizar este hábito
          </p>
        </div>
      )}

      {frequencyType === 'monthly' && (
        <div className="space-y-2">
          <Label htmlFor="monthDay">Dia do Mês</Label>
          <Input
            id="monthDay"
            type="number"
            min="1"
            max="31"
            value={monthDay}
            onChange={(e) => setMonthDay(e.target.value)}
            placeholder="Dia do mês (1-31)"
          />
          <p className="text-xs text-muted-foreground">
            O hábito aparecerá todo mês neste dia
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label>Ícone</Label>
        <div className="grid grid-cols-5 gap-2">
          {HABIT_ICONS.map(({ name: iconName, icon: IconComponent }) => (
            <button
              key={iconName}
              type="button"
              onClick={() => setIcon(iconName)}
              className={`p-2.5 rounded-lg border-2 transition-all hover:scale-105 ${icon === iconName ? 'border-primary bg-accent' : 'border-border'
                }`}
            >
              <IconComponent className="w-5 h-5 mx-auto" />
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Cor</Label>
        <div className="grid grid-cols-6 gap-2">
          {PRESET_COLORS.map((presetColor) => (
            <button
              key={presetColor.value}
              type="button"
              onClick={() => setColor(presetColor.value)}
              className="w-9 h-9 rounded-lg transition-transform hover:scale-110 border-2"
              style={{
                backgroundColor: presetColor.value,
                borderColor: color === presetColor.value ? '#000' : 'transparent',
              }}
              aria-label={presetColor.name}
            />
          ))}
        </div>
      </div>

      <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-semibold">Lembretes</Label>
          <span className="text-xs text-muted-foreground">(opcional)</span>
        </div>

        <div className="space-y-2">
          <Label htmlFor="reminderTime" className="text-sm">Primeiro lembrete</Label>
          <Input
            id="reminderTime"
            type="time"
            value={reminderTime}
            onChange={(e) => setReminderTime(e.target.value)}
            placeholder="ex: 09:00"
          />
        </div>

        {reminderTime && (
          <div className="space-y-2">
            <Label htmlFor="reminderFrequency" className="text-sm">Frequência</Label>
            <Select value={reminderFrequency} onValueChange={setReminderFrequency}>
              <SelectTrigger id="reminderFrequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="once">Uma vez ao dia</SelectItem>
                <SelectItem value="interval">A cada X horas</SelectItem>
                <SelectItem value="multiple">Horários específicos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {reminderTime && reminderFrequency === 'interval' && (
          <div className="space-y-2">
            <Label htmlFor="reminderInterval" className="text-sm">Repetir a cada</Label>
            <div className="flex gap-2">
              <Input
                id="reminderInterval"
                type="number"
                value={reminderInterval}
                onChange={(e) => setReminderInterval(e.target.value)}
                placeholder="2"
                min="1"
                max="12"
                className="w-20"
              />
              <span className="flex items-center text-sm text-muted-foreground">horas</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Exemplo: A partir de {reminderTime}, lembrar a cada {reminderInterval}h
            </p>
          </div>
        )}

        {reminderTime && (
          <div className="text-xs text-muted-foreground p-2 bg-blue-50 dark:bg-blue-950/30 rounded border border-blue-200 dark:border-blue-800/30">
            <p className="font-medium text-blue-700 dark:text-blue-400 mb-1">💡 Como funciona:</p>
            <ul className="space-y-0.5 ml-4 list-disc text-blue-700 dark:text-blue-300">
              {reminderFrequency === 'once' && (
                <li>Você receberá 1 lembrete por dia às {reminderTime}</li>
              )}
              {reminderFrequency === 'interval' && (
                <>
                  <li>Primeiro lembrete: {reminderTime}</li>
                  <li>Próximos lembretes: a cada {reminderInterval} hora(s)</li>
                  <li>Lembretes param quando você completar o hábito</li>
                </>
              )}
              {reminderFrequency === 'multiple' && (
                <li>Funcionalidade em desenvolvimento</li>
              )}
            </ul>
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-2 sticky bottom-0 bg-background">
        <Button type="submit" className="flex-1">
          {initialData ? 'Atualizar' : 'Criar'} Hábito
        </Button>
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        )}
      </div>
    </form>
  );
};

export default HabitForm;
